import {
	Component,
	Input,
	OnInit
}                            from '@angular/core';
import { BaseComponent }     from '@app/core/bases/base-component/base.component';
import {
	Address,
	AddressProps,
	IAddressPairCheckItem
}                            from '@app/shared/components/address/models/address.model';
import { ToEmptyStringPipe } from '@app/shared/pipes/to-empty-string.pipe';
import { UiService }         from '@app/shared/services/ui.service';
import { takeUntil }         from 'rxjs/operators';

@Component( {
	selector: 'app-address',
	templateUrl: './address.component.html',
	styleUrls: [ './address.component.scss' ]
} )
export class AddressComponent extends BaseComponent implements OnInit {

	noAddressDataExist: boolean;

	country: string;

	@Input() address: Address;
	@Input() alternativeEmptyAddressTextOutput: string;
	@Input() showAdditionalData = false;
	@Input() showAsNewLines = false;
	@Input() showCity = true;
	@Input() showCountry = true;
	@Input() showFederalState = false;
	@Input() showHouseNumber = true;
	@Input() showPostalCode = true;
	@Input() showStreet = true;

	@Input( 'updateCountryName' )
	set updateCountryName( runUpdate: boolean ) {
		if ( runUpdate ) {
			this.updateCountryNameToActiveLanguage();
		}
	}

	constructor(
		public toEmptyString: ToEmptyStringPipe,
		public uiService: UiService
	) {
		super();
	}

	private doesNotAddressPairsExist( ...addressPairCheckList: IAddressPairCheckItem[] ): boolean {

		let doesNotExist = false;
		addressPairCheckList.forEach( ( addressPairCheckItem: IAddressPairCheckItem ) => {
			if ( addressPairCheckItem.isSetToShow && !doesNotExist ) {
				doesNotExist = addressPairCheckItem.addressPairKeysList.some( ( addressProps: AddressProps ) => {
					return addressProps.some( ( key: string ) => {
						return this.address[ key ] === null || this.address[ key ] === undefined;
					} );
				} );
			} else {
				doesNotExist = true;
			}
		} );

		return doesNotExist;
	}

	private doesNoAddressDataExist(): boolean {
		let streetAndHouseNumberNotExist;
		let additionalDataNotExist;
		let postalCodeNotExist;
		let cityNotExist;
		let stateNotExist;
		let countryNotExist;
		this.noAddressDataExist = false;

		// STREET
		streetAndHouseNumberNotExist = this.doesNotAddressPairsExist(
			{
				addressPairKeysList: [
					[ 'street', 'houseNumber' ]
				],
				isSetToShow: this.showStreet || this.showHouseNumber
			}
		);

		// ADDITIONAL DATA
		additionalDataNotExist = this.doesNotAddressPairsExist(
			{
				addressPairKeysList: [
					[ 'additionalData' ]
				],
				isSetToShow: this.showAdditionalData
			}
		);

		// POSTAL
		postalCodeNotExist = this.doesNotAddressPairsExist(
			{
				addressPairKeysList: [
					[ 'postalCode' ]
				],
				isSetToShow: this.showPostalCode
			}
		);

		// CITY
		cityNotExist = this.doesNotAddressPairsExist(
			{
				addressPairKeysList: [
					[ 'city', 'street' ],
					[ 'city', 'state' ],
					[ 'city', 'countryCode' ]
				],
				isSetToShow: this.showCity
			}
		);

		// STATE
		stateNotExist = this.doesNotAddressPairsExist(
			{
				addressPairKeysList: [
					[ 'state', 'countryCode' ],
					[ 'state' ]
				],
				isSetToShow: this.showFederalState
			}
		);

		// COUNTRY
		countryNotExist = this.doesNotAddressPairsExist(
			{
				addressPairKeysList: [
					[ 'countryCode', 'city' ]
				],
				isSetToShow: this.showCountry
			}
		);

		this.noAddressDataExist = streetAndHouseNumberNotExist &&
															additionalDataNotExist &&
															postalCodeNotExist &&
															cityNotExist &&
															stateNotExist &&
															countryNotExist;

		return this.noAddressDataExist;
	}

	private updateCountryNameToActiveLanguage(): void {
		this.country = this.getI18nString( false, `countries.${ this.address.countryCode }` );
	}

	ngOnInit() {
		this.logger.trace( 'address: ', this.address );

		if ( this.address === null ) {
			this.address = this.toEmptyString.transform( new Address() );
		} else if ( !this.doesNoAddressDataExist() ) {

			if ( this.address.countryCode ) {
				this.updateCountryNameToActiveLanguage();

				this.uiService.languageHasChanged$
				.pipe( takeUntil( this.ngUnsubscribe ) )
				.subscribe( ( lang: string ) => this.updateCountryNameToActiveLanguage() );
			}
		}
	}
}
